import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { accessService, type MeResponse } from "@/services/accessService";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";

// ─────────────────────────────────────────────────────────────────────────────
// Context shape
// ─────────────────────────────────────────────────────────────────────────────

interface PermissionsContextType {
  /** Set of granted permission names, e.g. "branch:view", "role:manage" */
  permissions: Set<string>;
  /** Keycloak role names held by the user */
  roles: string[];
  /** User sub / email / name from /me */
  me: MeResponse | null;
  /** True while the first /me call is in flight */
  loading: boolean;
  /** Has a given permission? */
  can: (permission: string) => boolean;
  /** Force re-fetch (call after token refresh on role-change) */
  refresh: () => void;
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export const PermissionsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authenticated, token } = useKeycloak();
  const [me, setMe] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const lastToken = useRef<string | undefined>(undefined);

  const fetchMe = useCallback(async () => {
    try {
      const data = await accessService.getMe();
      setMe(data);
    } catch (err) {
      console.warn("[PermissionsProvider] /me failed:", err);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch whenever the token changes (covers initial load + post-role-grant refresh)
  useEffect(() => {
    if (!authenticated || !token) return;
    if (token === lastToken.current) return;
    lastToken.current = token;
    void fetchMe();
  }, [authenticated, token, fetchMe]);

  const permissions = useMemo<Set<string>>(
    () => new Set(me?.permissions ?? []),
    [me]
  );

  const roles = useMemo(() => me?.roles ?? [], [me]);

  const can = useCallback(
    (permission: string) => permissions.has(permission),
    [permissions]
  );

  const refresh = useCallback(() => {
    // Reset lastToken so the next token update triggers a re-fetch
    lastToken.current = undefined;
    void fetchMe();
  }, [fetchMe]);

  const value = useMemo<PermissionsContextType>(
    () => ({ permissions, roles, me, loading, can, refresh }),
    [permissions, roles, me, loading, can, refresh]
  );

  return (
    <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function usePermissions(): PermissionsContextType {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider");
  return ctx;
}
